import { useState, useEffect } from "react";
import { ArrowLeft, CreditCard, Plus, Trash2, Check, Loader2, X, Landmark } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface PaymentMethod {
  id: string;
  card_last_four: string | null;
  card_brand: string;
  cardholder_name: string;
  expiry_month: number | null;
  expiry_year: number | null;
  is_default: boolean;
  payment_type: string;
  iban: string | null;
}

interface SavedPaymentMethodsProps {
  isOpen: boolean;
  onClose: () => void;
}

const getCardIcon = (brand: string) => {
  return <CreditCard className="w-6 h-6" />;
};

const SavedPaymentMethods = ({ isOpen, onClose }: SavedPaymentMethodsProps) => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState<'card' | 'iban' | false>(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Card form state
  const [cardholderName, setCardholderName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");

  // IBAN form state
  const [ibanHolder, setIbanHolder] = useState("");
  const [ibanValue, setIbanValue] = useState("");

  useEffect(() => {
    if (isOpen) {
      fetchPaymentMethods();
    }
  }, [isOpen]);

  const fetchPaymentMethods = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('saved_payment_methods')
        .select('*')
        .eq('user_id', user.id)
        .order('is_default', { ascending: false });

      if (error) throw error;
      setPaymentMethods(data || []);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || "";
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    return parts.length ? parts.join(" ") : value;
  };

  const formatExpiryDate = (value: string) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    if (v.length >= 2) {
      return v.substring(0, 2) + "/" + v.substring(2, 4);
    }
    return v;
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    if (formatted.replace(/\s/g, "").length <= 16) {
      setCardNumber(formatted);
    }
  };

  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\//g, "");
    if (value.length <= 4) {
      setExpiryDate(formatExpiryDate(value));
    }
  };

  const handleAddCard = async () => {
    if (!cardholderName.trim()) {
      toast.error("Inserisci il nome del titolare");
      return;
    }
    if (cardNumber.replace(/\s/g, "").length < 16) {
      toast.error("Inserisci un numero di carta valido");
      return;
    }
    if (expiryDate.length < 5) {
      toast.error("Inserisci una data di scadenza valida");
      return;
    }

    setIsSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not found');

      const cardDigits = cardNumber.replace(/\s/g, "");
      const [month, year] = expiryDate.split("/");

      const { error } = await supabase
        .from('saved_payment_methods')
        .insert({
          user_id: user.id,
          payment_type: 'card',
          card_last_four: cardDigits.slice(-4),
          card_brand: 'visa',
          cardholder_name: cardholderName.trim(),
          expiry_month: parseInt(month),
          expiry_year: 2000 + parseInt(year),
          is_default: paymentMethods.length === 0
        });

      if (error) throw error;

      toast.success("Carta salvata con successo");
      setCardholderName("");
      setCardNumber("");
      setExpiryDate("");
      setShowAddForm(false);
      fetchPaymentMethods();
    } catch (error) {
      console.error('Error saving card:', error);
      toast.error("Errore nel salvataggio della carta");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddIban = async () => {
    if (!ibanHolder.trim()) {
      toast.error("Inserisci l'intestatario");
      return;
    }
    if (!ibanValue.trim() || ibanValue.replace(/\s/g, "").length < 15) {
      toast.error("Inserisci un IBAN valido");
      return;
    }

    setIsSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not found');

      const { error } = await supabase
        .from('saved_payment_methods')
        .insert({
          user_id: user.id,
          payment_type: 'iban',
          cardholder_name: ibanHolder.trim(),
          iban: ibanValue.replace(/\s/g, "").toUpperCase(),
          card_brand: 'iban',
          is_default: paymentMethods.length === 0
        });

      if (error) throw error;

      toast.success("IBAN salvato con successo");
      setIbanHolder("");
      setIbanValue("");
      setShowAddForm(false);
      fetchPaymentMethods();
    } catch (error) {
      console.error('Error saving IBAN:', error);
      toast.error("Errore nel salvataggio dell'IBAN");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Remove default from all cards
      await supabase
        .from('saved_payment_methods')
        .update({ is_default: false })
        .eq('user_id', user.id);

      // Set new default
      await supabase
        .from('saved_payment_methods')
        .update({ is_default: true })
        .eq('id', id);

      toast.success("Carta predefinita aggiornata");
      fetchPaymentMethods();
    } catch (error) {
      console.error('Error setting default:', error);
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const { error } = await supabase
        .from('saved_payment_methods')
        .delete()
        .eq('id', deleteId);

      if (error) throw error;

      toast.success("Carta eliminata");
      setDeleteId(null);
      fetchPaymentMethods();
    } catch (error) {
      console.error('Error deleting card:', error);
      toast.error("Errore nell'eliminazione");
    }
  };

  return (
    <>
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="h-[90vh] bg-background/95 backdrop-blur-xl border-t border-border/50">
          <DrawerHeader className="relative border-b border-border/30 pb-4">
            <button
              onClick={() => {
                if (showAddForm) {
                  setShowAddForm(false);
                } else {
                  onClose();
                }
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-muted/50 transition-all duration-300 hover:scale-110 active:scale-90"
            >
              <ArrowLeft className="w-5 h-5 text-foreground" />
            </button>
            <DrawerTitle className="text-xl font-bold text-center">
              {showAddForm === 'card' ? "Aggiungi Carta" : showAddForm === 'iban' ? "Aggiungi IBAN" : "Metodi di Pagamento"}
            </DrawerTitle>
          </DrawerHeader>

          <div className="flex flex-col p-4 overflow-y-auto flex-1">
            {showAddForm === 'card' ? (
              <div className="space-y-5">
                <div className="bg-primary/10 border border-primary/30 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-primary text-lg">🔒</span>
                    <div>
                      <p className="text-sm font-medium text-primary">Pagamento Sicuro</p>
                      <p className="text-xs text-primary/80 mt-1">I tuoi dati sono protetti con crittografia SSL.</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Nome e Cognome del titolare</label>
                  <input type="text" value={cardholderName} onChange={(e) => setCardholderName(e.target.value)} placeholder="Mario Rossi" disabled={isSaving} className="w-full px-4 py-3.5 rounded-xl bg-muted/50 border border-border/50 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 disabled:opacity-50" />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Numero della Carta</label>
                  <input type="text" value={cardNumber} onChange={handleCardNumberChange} placeholder="1234 5678 9012 3456" disabled={isSaving} className="w-full px-4 py-3.5 rounded-xl bg-muted/50 border border-border/50 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 disabled:opacity-50 font-mono tracking-wider" />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Scadenza (MM/AA)</label>
                  <input type="text" value={expiryDate} onChange={handleExpiryChange} placeholder="MM/AA" disabled={isSaving} className="w-full px-4 py-3.5 rounded-xl bg-muted/50 border border-border/50 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 disabled:opacity-50 font-mono" />
                </div>

                <button onClick={handleAddCard} disabled={isSaving} className="w-full py-4 px-6 bg-primary text-primary-foreground font-semibold rounded-xl transition-all duration-300 hover:bg-primary/90 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 flex items-center justify-center gap-2">
                  {isSaving ? (<><Loader2 className="w-5 h-5 animate-spin" /><span>Salvataggio...</span></>) : (<><Check className="w-5 h-5" /><span>Salva Carta</span></>)}
                </button>
              </div>
            ) : showAddForm === 'iban' ? (
              <div className="space-y-5">
                <div className="bg-primary/10 border border-primary/30 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <Landmark className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-primary">Bonifico Bancario</p>
                      <p className="text-xs text-primary/80 mt-1">Inserisci i dati del conto corrente per il pagamento tramite IBAN.</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Intestatario</label>
                  <input type="text" value={ibanHolder} onChange={(e) => setIbanHolder(e.target.value)} placeholder="Mario Rossi" disabled={isSaving} className="w-full px-4 py-3.5 rounded-xl bg-muted/50 border border-border/50 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 disabled:opacity-50" />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">IBAN</label>
                  <input type="text" value={ibanValue} onChange={(e) => setIbanValue(e.target.value.toUpperCase())} placeholder="IT60X0542811101000000123456" disabled={isSaving} className="w-full px-4 py-3.5 rounded-xl bg-muted/50 border border-border/50 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 disabled:opacity-50 font-mono tracking-wider" />
                </div>

                <button onClick={handleAddIban} disabled={isSaving} className="w-full py-4 px-6 bg-primary text-primary-foreground font-semibold rounded-xl transition-all duration-300 hover:bg-primary/90 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 flex items-center justify-center gap-2">
                  {isSaving ? (<><Loader2 className="w-5 h-5 animate-spin" /><span>Salvataggio...</span></>) : (<><Check className="w-5 h-5" /><span>Salva IBAN</span></>)}
                </button>
              </div>
            ) : (
              <>
                {loading ? (
                  <div className="flex-1 flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <>
                    <div className="space-y-3 mb-4">
                      {paymentMethods.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                          <CreditCard className="w-12 h-12 text-muted-foreground mb-4" />
                          <p className="text-muted-foreground">Nessuna carta salvata</p>
                          <p className="text-sm text-muted-foreground/70 mt-1">
                            Aggiungi una carta per pagamenti più rapidi
                          </p>
                        </div>
                      ) : (
                        paymentMethods.map((method) => (
                          <div
                            key={method.id}
                            className={`p-4 rounded-xl border backdrop-blur-sm transition-all duration-300 ${
                              method.is_default
                                ? 'bg-primary/5 border-primary/30'
                                : 'bg-card/80 border-border/50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`p-2.5 rounded-xl ${
                                  method.is_default ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                                }`}>
                                  {method.payment_type === 'iban' ? <Landmark className="w-6 h-6" /> : getCardIcon(method.card_brand)}
                                </div>
                                <div>
                                  <p className="font-semibold text-foreground">
                                    {method.payment_type === 'iban' 
                                      ? `IBAN ••••${method.iban?.slice(-4) || ''}` 
                                      : `•••• ${method.card_last_four}`}
                                  </p>
                                  <p className="text-sm text-muted-foreground">
                                    {method.cardholder_name}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {method.is_default && (
                                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                                    Predefinita
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="mt-3 pt-3 border-t border-border/30 flex items-center justify-between">
                              <span className="text-sm text-muted-foreground">
                                {method.payment_type === 'iban' 
                                  ? 'Bonifico Bancario' 
                                  : `Scade: ${String(method.expiry_month).padStart(2, '0')}/${(method.expiry_year || 0) % 100}`}
                              </span>
                              <div className="flex items-center gap-2">
                                {!method.is_default && (
                                  <button
                                    onClick={() => handleSetDefault(method.id)}
                                    className="px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg transition-all duration-300"
                                  >
                                    Imposta predefinita
                                  </button>
                                )}
                                <button
                                  onClick={() => setDeleteId(method.id)}
                                  className="p-2 text-destructive hover:bg-destructive/10 rounded-lg transition-all duration-300"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowAddForm('card')}
                        className="flex-1 py-4 px-4 bg-muted hover:bg-muted/80 text-foreground font-medium rounded-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 border border-border/50"
                      >
                        <CreditCard className="w-5 h-5" />
                        <span>Aggiungi Carta</span>
                      </button>
                      <button
                        onClick={() => setShowAddForm('iban')}
                        className="flex-1 py-4 px-4 bg-muted hover:bg-muted/80 text-foreground font-medium rounded-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 border border-border/50"
                      >
                        <Landmark className="w-5 h-5" />
                        <span>Aggiungi IBAN</span>
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </DrawerContent>
      </Drawer>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la carta?</AlertDialogTitle>
            <AlertDialogDescription>
              Questa azione non può essere annullata. La carta verrà rimossa dai tuoi metodi di pagamento salvati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default SavedPaymentMethods;